import { Alert as Notification } from '@/interfaces/alert';
import BaseModal from '@/modal/baseModal';
import SubmitButton from '@/ui-kit/SubmitButton';
import { Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import userServices from '@/api/userServices';
import { enqueueSnackbar } from 'notistack';
import endpoints from '@/api/endpoints';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch } from '@/app/hooks';
import { decreaseUnreadAlertsCount, increaseUnreadAlertsCount } from '@/features/user/userSlice';

export const WebNotifications: React.FC = () => {
  const { wssURL } = endpoints;
  const SOCKET_URL = wssURL(String(Cookies.get('accessToken')));
  const IN_APP = 'IN_APP_MESSAGE';

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const dispatch = useAppDispatch();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  const location = useLocation();
  const [isConnected, setIsConnected] = useState(false);
  const retryIntervalRef = useRef(1000);
  const socketRef = useRef<WebSocket | null>(null);

  const connectWebSocketClient = () => {
    if (socketRef.current) return;

    const socket = new WebSocket(SOCKET_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      setIsConnected(true);
      retryIntervalRef.current = 1000;
    };

    socket.onmessage = event => {
      try {
        const notify = JSON.parse(event.data);
        setShowNotification(true);
        dispatch(increaseUnreadAlertsCount());
        setNotifications(prevNotifications => [...prevNotifications, notify]);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    socketRef.current.onclose = event => {
      if (event.wasClean) {
        console.log(`Connection closed, code: ${event.code}, reason: ${event.reason}`);
      } else {
        handleReconnect();
      }
    };

    socket.onerror = error => {
      console.error('WebSocket error:', error);
      socket.close();
    };

    const handleReconnect = () => {
      const retryInterval = retryIntervalRef.current;
      console.log(`Reconnecting in ${retryInterval / 1000} seconds...`);
      setTimeout(() => {
        if (!isConnected) {
          retryIntervalRef.current = Math.min(retryInterval * 2, 30000);
          connectWebSocketClient();
        }
      }, retryInterval);
    };
  };

  const handleCloseConnection = () => {
    if (socketRef.current) {
      socketRef.current?.close(1000, 'Client closed connection');
      socketRef.current = null;
    }
  };

  const handleRead = (notification: Notification) => {
    userServices
      .readNotification(String(notification?.id))
      .then(() => dispatch(decreaseUnreadAlertsCount({})))
      .catch(err => enqueueSnackbar(err.data.message[currentLanguage], { variant: 'notification', type: 'error' }))
      .finally(() => {
        removeNotification(notification.alertId);
        setShowNotification(false);
      });
  };

  const removeNotification = (alertId: string) => {
    setNotifications(prevNotifications => prevNotifications.filter(notification => notification.alertId !== alertId));
  };

  const closeNotification = (e: any, notification: Notification) => {
    e.stopPropagation();
    removeNotification(notification.alertId);
  };

  const redirectToAlert = (e: any, notification: Notification) => {
    closeNotification(e, notification);
    navigate('/alerts');
  };

  useEffect(() => {
    Cookies.get('accessToken') ? connectWebSocketClient() : handleCloseConnection();

    if (location.pathname === '/log-in') {
      setShowNotification(false);
      setNotifications([]);
      handleCloseConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <>
      {notifications.map((notification, index) =>
        notification?.deliveryMethod === IN_APP ? (
          <>
            {showNotification && (
              <BaseModal hideCloseIcon={true} width='30rem'>
                <div id='headerBlock' className={clsx(styles.modal__head, 'py-6 items-center w-full pl-6')}>
                  <h6 id='title' className='mr-auto'>
                    {notification.title}
                  </h6>
                </div>
                <div id='messageBlock' className={styles.modal__body}>
                  <p id='messageBody'>
                    {notification.message.split('\n').map(line => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                <div id='buttonBlock' className={styles.modal__buttonSection}>
                  <SubmitButton
                    id='submitMessage'
                    inputStyle='!w-auto'
                    label={t('general.gotIt')}
                    onClick={() => handleRead(notification)}
                  />
                </div>
              </BaseModal>
            )}
          </>
        ) : (
          <Snackbar
            key={notification.alertId}
            open
            autoHideDuration={10000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            style={{ '--dynamic-position': `${8 + index * 80}px` } as React.CSSProperties}
            className={styles.notification}
          >
            <Alert
              className='!max-w-[40rem] !bg-[#FFFFFF] !text-[black] !shadow-[0px_4px_4px_rgba(0,_0,_0,_0.25)]'
              onClose={e => closeNotification(e, notification)}
              onClick={e => redirectToAlert(e, notification)}
            >
              <p className='mb-2'>{notification.title}:</p>
              {notification.message}
            </Alert>
          </Snackbar>
        )
      )}
    </>
  );
};
